import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import constants from '@Common/Core/Constant';
import { properties } from '@Common/Core/Properties';

export interface ISearchParameters extends IControlParameters {
    blockElement?: boolean;
    isDesktop?: boolean;
    placeholder?: string;
    delay?: number;

    searchIcon?: string;
    clearIcon?: string;
}

export class SearchControl extends BaseControl<ISearchParameters> {
    private timerId: number;
    private value: string;
    private delay: number;

    private readonly input: JQuery<HTMLInputElement>;
    private readonly clearButton: JQuery<HTMLElement>;
    private onChangeHandler = _.noop;

    constructor(param: ISearchParameters) {
        super(param);

        this._param = $.extend(
            {
                css: null,
                className: '',
                width: 'auto',
                height: '30px',

                delay: 0,
                blockElement: true,
                isDesktop: properties.isDesktopBrowser,
                placeholder: null
            },
            param
        );

        this.delay = this._param.delay;

        this._root = $('<span class="search-control-wrapper"/>')
            .css({
                width: this._param.width,
                height: this._param.height
            })
            .toggleClass('block', this._param.blockElement);

        this.input = $('<input class="search-control" autocomplete="new-password" type="text"/>')
            .css('width', this._param.width)
            .css('height', this._param.height)
            .toggleClass('block', this._param.blockElement)
            .keyup(e => {
                if (e.keyCode === constants.keyCodes.ESCAPE) {
                    this.input.val('');
                }
                const currentValue = this.getValue();
                if (this.value !== currentValue) {
                    this.change(e);
                }
            })
            .on('paste cut', e => {
                const currentValue = this.getValue();
                if (this.value !== currentValue) {
                    this.change(e);
                }
            })
            .click(e => {
                const currentValue = this.getValue();
                if (this.value !== currentValue) {
                    this.change(e, true);
                }
            })
            .appendTo(this._root) as JQuery<HTMLInputElement>;

        this.clearButton = $('<span class="search-clear-button"/>')
            .click(e => {
                this.input.focus();
                if (!this.input.val()) {
                    return;
                }
                this.input.val('');
                this.change(e, true);
            })
            .appendTo(this._root);

        this._init();
        this.setValue('');
    }

    public getValue(): string {
        return this.input[0].value;
    }

    public setValue(data: string): void {
        this.value = data;
        this.input.val(data);
        this.change(undefined, true);
    }

    public adjust(width?: number, height?: number): void {
        const w = width || this._param.width;
        const h = height || this._param.height;
        this.input.outerWidth(w).outerHeight(h);
        this._root.outerWidth(w).outerHeight(h);
    }

    public focus(): void {
        this.input.focus();
    }

    public setState(enabled: boolean): void {
        this.input.attr('disabled', !enabled as any);
        this.input.toggleClass('disabled', !enabled);
    }

    public setDelay(time: number): void {
        this.delay = time;
    }

    public onChange(fn: (e: JQuery.Event) => void): void {
        if (_.isFunction(fn)) {
            this.onChangeHandler = fn;
        }
    }

    protected _init(): void {
        if (this._param.placeholder) {
            this.input.attr('placeholder', this._param.placeholder);
        }

        if (this._param.className) {
            this.input.addClass(this._param.className);
        }

        if (this._param.css) {
            this.input.css(this._param.css);
        }

        this.setEmptyState(true);
    }

    private change(e?: JQuery.Event, ignoreDelay?: boolean): void {
        this.value = this.getValue();

        if (this.delay > 0 && !ignoreDelay) {
            clearTimeout(this.timerId);
            this.timerId = setTimeout(() => {
                this.setEmptyState(this.value === '');
                this.onChangeHandler(e);
            }, this.delay);
        } else {
            this.setEmptyState(this.value === '');
            this.onChangeHandler(e);
        }
    }

    private setEmptyState(isEmpty: boolean) {
        this._root.toggleClass('empty-search', isEmpty);
        this.clearButton.css({
            background: isEmpty ? this._param.searchIcon : this._param.clearIcon
        });
    }
}
